import React from 'react';
import EntityTitle from '@/components/EntityModal/EntityTitle';
import Menu from '@/components/EntityModal/Menu';
import Tabs from '@/components/EntityModal/Organization/Header/Tabs';
import { useOrganization, useOnUpdate, useOnDelete, useVoip } from '@/contexts/organizationModal';
import { canEditOrganization } from '@/policies';
import { organizationEditPath } from '@/routes';
import SelectOwnerUser from '@/components/EntityModal/SelectOwnerUser';
import HeaderAvatar from '@/components/EntityModal/HeaderAvatar';
import RestrictWarningBadge from '@/components/EntityModal/RestrictWarningBadge';
import AddDealPopover from '@/components/QuickForms/AddDealPopover';
import Skeleton from '@/components/Skeleton';
import SkeletonWrapper from '@/components/SkeletonWrapper';
import SelectCategory from '@/components/EntityModal/SelectCategory';
import AvailablePhoneField from '@/components/EntityModal/AvailablePhoneField';
import EmailField from '@/components/EntityModal/EmailField';
import DotList from '@/components/DotList';
import Button from '@/components/Button';
import Icon from '@/components/Icon';
import Rating from '@/components/Rating';

const ENTITY_TYPE = 'organization';

function Header() {
  const organization = useOrganization();
  const onUpdate = useOnUpdate();
  const onDelete = useOnDelete();
  const voip = useVoip();

  const isLoading = !organization;

  return (
    <div className='rounded-top px-4 pt-4'>
      <div className='mb-4 d-flex justify-content-between'>
        <div className='d-flex flex-fill'>
          <SkeletonWrapper
            isLoading={isLoading}
            SkeletonComponent={() => <Skeleton width={64} height={64} className='me-3' />}
          >
            <HeaderAvatar
              entityType={ENTITY_TYPE}
              avatarUrl={organization?.logo?.url}
            />
          </SkeletonWrapper>

          <div className='py-1 flex-fill'>
            <div className='w-100 d-flex align-items-center mb-1'>
              <div className='flex-fill d-flex align-items-center'>
                <SkeletonWrapper
                  isLoading={isLoading}
                  SkeletonComponent={() => <Skeleton width={250} height={24} />}
                >
                  <EntityTitle
                    className='min-width-5'
                    onUpdate={onUpdate}
                    entity={{ title: organization?.name }}
                  />
                  <div className='ms-2'>
                    <Rating
                      value={organization?.ranking ?? 0}
                      onChange={(ranking) => onUpdate({ ranking })}
                    />
                  </div>
                </SkeletonWrapper>
              </div>

              <div>
                <SkeletonWrapper
                  isLoading={isLoading}
                  SkeletonComponent={() => <Skeleton width={50} height={20} />}
                >
                  <RestrictWarningBadge entity={organization} loading={false} />
                </SkeletonWrapper>
              </div>
            </div>

            <div className='w-100 d-flex align-items-center'>
              <div className='d-flex flex-fill align-items-center'>
                <DotList className='d-flex align-items-center'
                  items={[
                    <SkeletonWrapper
                      key={1}
                      isLoading={isLoading}
                      SkeletonComponent={() => <Skeleton width={160} height={15} />}
                    >
                      <SelectCategory
                        category={organization?.category}
                        canEditPolicy={canEditOrganization}
                        onUpdate={onUpdate}
                      />
                    </SkeletonWrapper>,
                    <SkeletonWrapper
                      key={2}
                      isLoading={isLoading}
                      SkeletonComponent={() => <Skeleton width={160} height={15} />}
                    >
                      <AvailablePhoneField
                        inputValue={organization?.contact?.whatsapp}
                        previewValue={organization?.contact?.availablePhone}
                        inputName='contact.whatsapp'
                        onEditEntity={(params, done) => onUpdate(params, done)}
                        entity={organization}
                        entityType='organization'
                        voip={voip}
                      />
                    </SkeletonWrapper>,

                    <SkeletonWrapper
                      key={3}
                      isLoading={isLoading}
                      SkeletonComponent={() => <Skeleton width={160} height={15} />}
                    >
                      <EmailField
                        inputValue={organization?.contact?.email}
                        inputName='contact.email'
                        onEditEntity={(params, done) => onUpdate(params, done)}
                        entityId={organization?.id}
                        entityType='organization'
                      />
                    </SkeletonWrapper>,
                    <SkeletonWrapper
                      key={4}
                      isLoading={isLoading}
                      SkeletonComponent={() => <Skeleton width={160} height={15} />}
                    >
                      <SelectOwnerUser
                        ownerUser={organization?.ownerUser}
                        canEditPolicy={canEditOrganization}
                        onUpdate={onUpdate}
                      />
                    </SkeletonWrapper>
                  ]} />
              </div>

              <div className='d-flex'>
                <SkeletonWrapper
                  isLoading={isLoading}
                  SkeletonComponent={() => (
                    <>
                      <Skeleton width={200} height={30} />

                      <Skeleton width={110} height={30} className='ms-3' />
                    </>
                  )}
                >
                  <AddDealPopover
                    entityId={organization?.id}
                    entityType={ENTITY_TYPE}
                  >
                    <Button
                      className='d-flex align-items-center text-nowrap mx-2 text-primary'
                      variant='light-primary'
                      size='sm'
                    >
                      <Icon name='plus' className='me-2' size='fixed' />
                      Adicionar negócio
                    </Button>
                  </AddDealPopover>
                  <Menu
                    entity={organization}
                    entityType={ENTITY_TYPE}
                    loading={false}
                    onDelete={onDelete}
                    editPath={organizationEditPath(organization?.id)}
                  />
                </SkeletonWrapper>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Tabs />
    </div>
  );
}

export default Header;
